import React, {useEffect, useRef} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {useGlobalSnackbar} from '~/src/context/GlobalSnackbarContext'
import {Spinner, Stack, VisuallyHidden} from '~/src/design-system/Primitives'
import {isEnabled, ORDER_ACCESS_INVITES_FLAG} from '~/src/featureFlags'
import {useSwitchAgency} from '~/src/hooks/useSwitchAgency'
import {useCurrentUser} from '~/src/store/slice-hooks'
import {useAppSelector} from '~/src/store/store-hooks'
import {
  BRANCH_LINK_PARAM,
  branchToSelectFromLink,
  searchWithoutBranchParam,
} from '~/src/utils/branchFromLink'
import {reportCatchError} from '~/src/utils/errorReporter'

const SwitchingStatus = styled(Stack)`
  padding: var(--space-12) 0;
`

/**
 * The routes a job order invitation email links to with `?agencyId=` (STFFLG-4184 Slice 5,
 * contract S5.1): the invitee's Manage Orders › Pending Orders and a job order's page. routeConfig
 * ids; AppRoutes' RouteRenderer puts these behind BranchFromLinkGate.
 */
export const BRANCH_LINK_ROUTE_IDS: ReadonlySet<string> = new Set([
  'agency-service-order-pending',
  'job-order-summary',
])

/**
 * Applies an email link's `?agencyId=` once per navigation, and answers whether the page may
 * render yet:
 * - a branch the user holds (home or `agencyAssignments`) that is not the selected one is selected
 *   first, through the header switcher's own useSwitchAgency, the path the in-app invitation
 *   notification takes; the page waits for it, so it never reads the previous branch's data (the
 *   order page would take that refusal for revoked access and leave);
 * - anything else (a branch the user does not hold, the selected one, an MSP or Platform user) is
 *   ignored: nothing is selected and no error is shown;
 * - either way the parameter is then dropped from the URL, replacing the entry (no history entry),
 *   and other parameters are kept. A switch that fails shows the switcher's error toast and the
 *   page opens in the branch already selected.
 * The route's permission check runs after this (AppRoutes' GuardedRoute), so it reads the selected
 * branch's role.
 */
export function useBranchFromLink(): boolean {
  // TODO(feature-flag): orderAccessInvites — remove the isEnabled check when the flag is deleted
  // (flag off: the parameter is left alone and the page renders at once, exactly as before).
  const flagOn = isEnabled(ORDER_ACCESS_INVITES_FLAG)
  const location = useLocation()
  const navigate = useNavigate()
  const switchAgency = useSwitchAgency()
  const {showError} = useGlobalSnackbar()
  const {currentUser} = useCurrentUser()
  const loggedInAgencyId = useAppSelector((state) => state.root.loggedInAgencyId)
  const role = useAppSelector((state) => state.root.backendRolePermissions)

  const linked = flagOn ? new URLSearchParams(location.search).get(BRANCH_LINK_PARAM) : null
  const target =
    linked === null
      ? null
      : branchToSelectFromLink(linked, {
          loggedInAgencyId,
          homeAgencyId: currentUser?.agencyId,
          agencyAssignmentIds: currentUser?.agencyAssignments?.map((a) => a.agencyId),
          roleCategory: role?.category,
          isPlatform: role?.isPlatform,
        })

  // The navigation already handled: StrictMode's second effect run, and the re-renders the switch
  // itself causes, must not switch again.
  const handledRef = useRef<string | null>(null)
  // A switch settles after its navigation may have been left (a nav click or Back while the spinner
  // shows): it then drops the parameter only if the gate is still mounted on the entry it handled,
  // so it never pulls the user back to the linked page. Unmount-only (not the main effect's own
  // cleanup, which also runs when `target` changes mid-switch); set again in the body so
  // StrictMode's replay restores it.
  const mountedRef = useRef(true)
  const locationKeyRef = useRef(location.key)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  useEffect(() => {
    locationKeyRef.current = location.key
  })

  useEffect(() => {
    if (linked === null) return
    const handledKey = location.key
    const key = `${handledKey}|${linked}`
    if (handledRef.current === key) return
    handledRef.current = key

    const dropParam = () =>
      navigate(
        {
          pathname: location.pathname,
          search: searchWithoutBranchParam(location.search),
          hash: location.hash,
        },
        {replace: true, state: location.state},
      )

    if (!target) {
      dropParam()
      return
    }

    void (async () => {
      try {
        await switchAgency(target)
      } catch (switchError) {
        reportCatchError('Failed to switch agency from a link:', switchError)
        showError('Failed to switch agency. Please try again.')
      } finally {
        if (mountedRef.current && locationKeyRef.current === handledKey) dropParam()
      }
    })()
  }, [linked, target, location, navigate, switchAgency, showError])

  // Never render the page while the parameter is in the URL, even when nothing is switched: the
  // replace that drops it would otherwise land after the page's own mount-time navigation (e.g.
  // GuardedRoute's <Navigate to="/unauthorized" replace />) and undo it, leaving an empty page in
  // production (StrictMode's replayed effects hide this in dev). One extra spinner frame instead.
  return linked === null
}

/** Renders `children` once an email link's branch is applied (useBranchFromLink). */
export function BranchFromLinkGate({children}: {children: React.ReactNode}): JSX.Element {
  const ready = useBranchFromLink()
  if (!ready) {
    return (
      <SwitchingStatus
        align="center"
        justify="center"
        role="status"
        data-testid="branch-from-link-switching">
        <Spinner aria-hidden="true" />
        <VisuallyHidden>Opening in your branch</VisuallyHidden>
      </SwitchingStatus>
    )
  }
  return <>{children}</>
}
